import {
  getAge,
  isValidEmail,
  isValidPhoneNumber,
} from "@b2bportal/core-utilities";
import { getZipError, nameRegex } from "@hopper-b2b/utilities";
import dayjs from "dayjs";
import { CommonFormErrorEnum, type Form } from "../types";
import { keyForError } from "./i18n";

export const required = <FormKey extends string>(
  i18nModule: string,
  values: Form<FormKey>["state"]["values"],
  key: FormKey
) =>
  !values[key]
    ? keyForError(i18nModule, key, CommonFormErrorEnum.BLANK)
    : false;

export const requiredEmail = <FormKey extends string>(
  i18nModule: string,
  values: Form<FormKey>["state"]["values"],
  key: FormKey
) => {
  const isRequired = required(i18nModule, values, key);
  if (isRequired) return isRequired;
  const invalidEmail = !isValidEmail(values[key]);
  if (invalidEmail)
    return keyForError(i18nModule, key, CommonFormErrorEnum.INVALID_EMAIL);
  return false;
};

export const requiredPhoneNumber = <FormKey extends string>(
  i18nModule: string,
  values: Form<FormKey>["state"]["values"],
  key: FormKey,
  countryCodeKey?: FormKey
) => {
  const isRequired = required(i18nModule, values, key);
  if (isRequired) return isRequired;
  const countryCode = countryCodeKey ? values[countryCodeKey] : "";
  const value = `${countryCode}${values[key]}`;
  const invalidEmail = !isValidPhoneNumber(value);
  if (invalidEmail)
    return keyForError(
      i18nModule,
      key,
      CommonFormErrorEnum.INVALID_PHONE_NUMBER
    );
  return false;
};

export const ageRange = <FormKey extends string>(
  i18nModule: string,
  values: Form<FormKey>["state"]["values"],
  key: FormKey,
  {
    minAge = 0,
    maxAge = 120,
  }: {
    minAge?: number;
    maxAge?: number;
  }
) => {
  const isRequired = required(i18nModule, values, key);
  if (isRequired) return isRequired;
  const value = values[key];

  // Deconstruct the date string into year, month, and day.
  // We expect the date to be in the format YYYY-MM-DD.
  const ageParts = value.split("-");
  const year = Number(ageParts[0]);
  const _month = ageParts[1];
  const day = Number(ageParts[2]);

  // only allow four digit years
  // Some date checks (e.g., new Date(year)) will allow 2 digit years,
  // but it doesn't match the format we use for the request.
  if (year.toString().length !== 4) {
    return keyForError(i18nModule, key, CommonFormErrorEnum.INVALID);
  }

  if (day > 31) {
    return keyForError(i18nModule, key, CommonFormErrorEnum.INVALID);
  }

  // Manually construct the date to ensure it matches our format.
  const age = getAge(value);

  if (typeof minAge === "number" && age < minAge) {
    return keyForError(i18nModule, key, CommonFormErrorEnum.MINIMUM_AGE);
  }
  if (typeof maxAge === "number" && age > maxAge) {
    return keyForError(i18nModule, key, CommonFormErrorEnum.INVALID);
  }

  return false;
};

export const requiredName = <FormKey extends string>(
  i18nModule: string,
  values: Form<FormKey>["state"]["values"],
  key: FormKey
) => {
  const isRequired = required(i18nModule, values, key);
  if (isRequired) return isRequired;
  const value = values[key];
  const isValidName = nameRegex.test(value);
  if (!isValidName)
    return keyForError(i18nModule, key, CommonFormErrorEnum.INVALID);
  return false;
};

export const requiredZip = <FormKey extends string>(
  i18nModule: string,
  values: Form<FormKey>["state"]["values"],
  key: FormKey,
  countryKey: FormKey
) => {
  const isRequired = required(i18nModule, values, key);
  if (isRequired) return isRequired;
  const value = values[key];
  const countryValue = values[countryKey];
  if (!countryValue) return false; // Skip zip validation if country is not provided
  const zipError = getZipError(value, countryValue);
  if (zipError)
    return keyForError(i18nModule, key, CommonFormErrorEnum.INVALID);
  return false;
};

export const requiredExpiry = <FormKey extends string>(
  i18nModule: string,
  values: Form<FormKey>["state"]["values"],
  key: FormKey
) => {
  const isRequired = required(i18nModule, values, key);
  if (isRequired) return isRequired;
  const value = values[key];

  // Validate MM/YY format
  const expiryRegex = /^(0[1-9]|1[0-2])\/\d{2}$/;
  if (!expiryRegex.test(value)) {
    return keyForError(i18nModule, key, CommonFormErrorEnum.INVALID);
  }

  // Parse expiry date using dayjs
  const [monthStr, yearStr] = value.split("/");
  const expiryDate = dayjs(`20${yearStr}-${monthStr}-01`); // First day of expiry month
  const currentDate = dayjs().startOf("month"); // First day of current month

  // Check if the expiry date is in the past
  if (expiryDate.isBefore(currentDate)) {
    return keyForError(i18nModule, key, CommonFormErrorEnum.INVALID);
  }

  return false;
};
